/**
 * The sector vocabulary: Marjorie's twelve tags as captured on the call of
 * 21 September, in the order she said them, and then four buckets the list's
 * own data needs and her twelve have no word for. One constant, so the written
 * list she sends can replace this one without a hunt through the code.
 *
 * The four are where the spreadsheet's free text actually sat. `software`,
 * `B2B software` and `enterprise software` were 109 funds between them and no
 * tag of hers covers any of it; `generalist` alone was 130, and it is a real
 * answer rather than a missing one — a fund with no sector focus. `Consumer`
 * and `Climate` are the same story at a smaller scale. They come after her
 * twelve, so the order still reads as hers with ours appended.
 *
 * A const tuple rather than a string array: the union type below is derived
 * from it, so a tag nobody added here cannot be spelled anywhere else.
 */
export const SECTOR_TAGS = [
    'Fintech',
    'InsurTech',
    'Defense',
    'Robotics',
    'Energy',
    'AI',
    'Logistics',
    'Construction',
    'Industrials',
    'Health',
    'Biotech/TechBio',
    'Deep Tech',
    'Mobility',
    'Software',
    'Consumer',
    'Climate',
    'Generalist',
];
/**
 * Which tag list a stored classification was made against. Bump it whenever
 * `SECTOR_TAGS` or a definition below changes: rows written under an older
 * version stop being read, and `pnpm sectors:classify` refuses any version but
 * this one, so a re-run is what brings the stored tags up to the new list.
 * Date-shaped by convention, not parsed.
 */
export const SECTOR_TAXONOMY_VERSION = '2026-10-16';
/**
 * What each tag means, one line each. The sector classifier puts these in its
 * prompt verbatim, so Marjorie's confirmation of the list is an edit here (and
 * a bump of the version above) and nowhere else.
 */
export const SECTOR_TAG_DEFINITIONS = {
    Fintech: 'Financial services built on software: payments, banking, lending, wealth, accounting, regtech.',
    InsurTech: 'Insurance products, distribution or underwriting built on technology.',
    Defense: 'Defence and national security technology for armed forces or governments. Not cybersecurity on its own.',
    Robotics: 'Machines that sense and act in the physical world: industrial, warehouse, surgical or service robots.',
    Energy: 'Generating, storing, trading or distributing energy: renewables, batteries, grid, energy management.',
    AI: 'The product is AI itself: models, AI infrastructure, or an application that could not exist without it. Not every company that uses AI.',
    Logistics: 'Moving and storing goods: freight, shipping, warehousing, last-mile delivery, supply chain software.',
    Construction: 'Building things: construction technology, building materials and methods. Not real estate or proptech.',
    Industrials: 'Manufacturing, industrial automation and machinery, industrial supply chains and the software that runs them. Not Deep Tech (novel science or hardware R&D) and not Logistics (moving goods).',
    Health: 'Healthcare: care delivery, digital health, medical devices, diagnostics, health software.',
    'Biotech/TechBio': 'Life sciences: drug discovery, therapeutics, synthetic biology, computational biology.',
    'Deep Tech': 'Science-heavy hardware and engineering: semiconductors, space, quantum, advanced materials, industrial hardware.',
    Mobility: 'Moving people: vehicles, electric vehicles, micromobility, ride-hailing, transit.',
    Software: 'Business software (B2B SaaS, developer tools, data, enterprise infrastructure) where no narrower tag fits.',
    Consumer: 'Products sold to consumers: e-commerce, marketplaces, consumer apps, media, gaming, consumer brands.',
    Climate: 'Decarbonisation and sustainability beyond energy itself: carbon removal, circular economy, sustainable food and materials.',
    Generalist: 'A fund with no sector focus. A fund answer, never a company answer.',
};
/**
 * The tags a company can be given. `Generalist` says a fund has no sector
 * focus; a company always does something, so it is not on offer.
 */
export const CLASSIFIABLE_SECTOR_TAGS = SECTOR_TAGS.filter((tag) => tag !== 'Generalist');
/** A stored tag read back as one of today's, or null for a word the list no longer has. */
export function parseSectorTag(value) {
    return SECTOR_TAGS.find((tag) => tag === value) ?? null;
}
/**
 * Dealroom's industry vocabulary in Marjorie's words. All 31 terms it publishes
 * on `/investors/filters` are named, including the ones that map to nothing:
 * a term listed with an empty answer is a decision somebody made, and a term
 * Dealroom adds later shows up as missing from this table rather than being
 * quietly bucketed.
 *
 * An industry maps only where the two words mean the same thing. Dealroom's
 * `security` is mostly cybersecurity and Marjorie's `Defense` is defence tech;
 * its `real estate` is proptech and her `Construction` is building. Tagging
 * across a gap like that would put a tag on a fund that never earned it, and a
 * wrong tag is worse than an absent one on a column whose whole job is to say
 * what a fund actually backs.
 *
 * The consequence, worth knowing before reading a thin column: Dealroom's
 * coarse industries cannot produce InsurTech, Defense, AI, Logistics,
 * Construction or Biotech/TechBio at all. Those six wait on a finer source than
 * the one `company.dealroom_industries` holds.
 */
const TAGS_BY_INDUSTRY = new Map([
    ['fintech', ['Fintech']],
    ['health', ['Health']],
    ['energy', ['Energy']],
    ['robotics', ['Robotics']],
    // Dealroom's one transport bucket holds both moving people and moving goods.
    // It is read as Mobility, because tagging a freight company Logistics and a
    // scooter company Mobility needs a distinction this vocabulary does not make.
    ['transportation', ['Mobility']],
    ['semiconductors', ['Deep Tech']],
    ['space', ['Deep Tech']],
    ['engineering and manufacturing equipment', ['Industrials']],
    // Named, and deliberately unmapped.
    ['enterprise software', []],
    ['food', []],
    ['marketing', []],
    ['media', []],
    ['real estate', []],
    ['security', []],
    ['education', []],
    ['telecom', []],
    ['fashion', []],
    ['home living', []],
    ['jobs recruitment', []],
    ['travel', []],
    ['gaming', []],
    ['sports', []],
    ['wellness beauty', []],
    ['legal', []],
    ['kids', []],
    ['event tech', []],
    ['music', []],
    ['hosting', []],
    ['chemicals', []],
    ['consumer electronics', []],
    ['dating', []],
]);
/**
 * The tags a set of Dealroom industries earns, deduplicated and in taxonomy
 * order, so two funds carrying the same tags read the same way.
 */
export function sectorTagsForIndustries(industries) {
    const found = new Set();
    for (const industry of industries) {
        for (const tag of TAGS_BY_INDUSTRY.get(industry.trim().toLowerCase()) ?? [])
            found.add(tag);
    }
    return SECTOR_TAGS.filter((tag) => found.has(tag));
}
/**
 * One company's tags: what the sector classifier read off its press, where it
 * has confident tags under the current list, and the Dealroom industry mapping
 * otherwise. `classified` is already narrowed to the current taxonomy version
 * and the confidence floor; empty or null means "nothing confident", which
 * falls back rather than reading as "no sector".
 */
export function sectorTagsForCompany(company) {
    const classified = (company.classified ?? []).flatMap((value) => {
        const tag = parseSectorTag(value);
        return tag === null ? [] : [tag];
    });
    if (classified.length === 0)
        return sectorTagsForIndustries(company.industries);
    return SECTOR_TAGS.filter((tag) => classified.includes(tag));
}
/**
 * The other direction: the Dealroom industries that earn any of these tags,
 * lower-case as `company.dealroom_industries` holds them. Round search filters
 * companies with it, so "Health" finds a company Dealroom calls `health`
 * whether or not its one `sector_id` bucket was ever set.
 */
export function industriesForSectorTags(tags) {
    const wanted = new Set(tags);
    return [...TAGS_BY_INDUSTRY]
        .filter(([, earned]) => earned.some((tag) => wanted.has(tag)))
        .map(([industry]) => industry);
}
/**
 * Every sector name the free-text vocabulary grew, and the tags it becomes.
 *
 * Marjorie, 29 September: "Some look a bit random like 'network effects' in
 * 'industry'. And we don't need 'financial services' and 'fintech'." Both
 * halves of that are here. A name that says how a fund invests rather than
 * what it invests in — a thesis (`network effects`, `impact`, `crossover`), an
 * instrument (`debt`, `secondaries`), a stage (`growth`) — maps to nothing and
 * is dropped, because it was never a sector. A name that is another spelling
 * of a tag (`financial services`, `defence`, `digital health`) folds into it.
 *
 * Two are dropped on the same ground the Dealroom table above already refuses
 * them: `proptech` is the built environment and `Construction` is building,
 * and `security` is mostly cybersecurity while `Defense` is defence tech. One
 * rule for both sources, so a tag means the same thing whoever wrote it.
 *
 * Keys are lower-case; look a name up through `sectorTagsForName`, which folds
 * the case, because the spreadsheet wrote `AI` and `B2B software` in caps.
 * A name this table does not carry is left exactly as it is rather than
 * dropped — the consolidation only ever moves what it was told about.
 */
export const LEGACY_SECTOR_TO_TAGS = {
    // Straight renames and near-spellings.
    fintech: ['Fintech'],
    'financial services': ['Fintech'],
    regtech: ['Fintech'],
    insurtech: ['InsurTech'],
    defence: ['Defense'],
    energy: ['Energy'],
    ai: ['AI'],
    logistics: ['Logistics'],
    'supply chain': ['Logistics'],
    construction: ['Construction'],
    health: ['Health'],
    'digital health': ['Health'],
    medtech: ['Health'],
    biotech: ['Biotech/TechBio'],
    'life sciences': ['Biotech/TechBio'],
    mobility: ['Mobility'],
    generalist: ['Generalist'],
    // Deep tech and its neighbours. `space` follows the Dealroom table above.
    deeptech: ['Deep Tech'],
    space: ['Deep Tech'],
    // The cells behind `industrial` are industrial automation, built-environment
    // hardware and "Deeptech / industrial": manufacturing and machinery, which
    // has its own tag since the 16 October confirmation.
    industrial: ['Industrials'],
    // The software bucket.
    software: ['Software'],
    'b2b software': ['Software'],
    'enterprise software': ['Software'],
    saas: ['Software'],
    'developer tools': ['Software'],
    // `infrastructure` is AI, data and enterprise infra in nearly every cell it
    // came from: software, at the layer below the application.
    infrastructure: ['Software'],
    data: ['Software'],
    // The consumer bucket.
    consumer: ['Consumer'],
    marketplaces: ['Consumer'],
    'e-commerce': ['Consumer'],
    ecommerce: ['Consumer'],
    media: ['Consumer'],
    gaming: ['Consumer'],
    // The climate bucket.
    climate: ['Climate'],
    cleantech: ['Climate'],
    sustainability: ['Climate'],
    // Not sectors: a thesis, an instrument, a stage, or a word for a market this
    // vocabulary has no tag for.
    'network effects': [],
    impact: [],
    crossover: [],
    growth: [],
    debt: [],
    secondaries: [],
    proptech: [],
    security: [],
    education: [],
    'agri-food': [],
};
/**
 * The tags a free-text sector name becomes: its own if it is already a tag,
 * the table's answer if it is a name the table carries, and null where it is
 * neither — which means "nobody has decided about this name", not "drop it".
 */
export function sectorTagsForName(name) {
    const folded = name.trim().toLowerCase();
    const tag = SECTOR_TAGS.find((candidate) => candidate.toLowerCase() === folded);
    if (tag !== undefined)
        return [tag];
    return LEGACY_SECTOR_TO_TAGS[folded] ?? null;
}
//# sourceMappingURL=sectorTaxonomy.js.map